import { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import {
  EXCL_DURACIONES_PREDEF,
  MODELOS_ENCARGO_APP,
  calcularHonorariosDesdeVentaYPct,
  calcularHonorariosDesdeVentaYProp,
  esEncargoHonorariosIncluidos,
  esTipoEncargoExclusiva,
  exclusivaFechaMasMeses,
  exclusivaHoyIso,
  exclusivaLabelMeses,
  exclusivaMesesDeDuracion,
  exclusivaMesesEntreFechas,
  formatearPrecioUi,
  parsePrecioUi,
  pctHonorariosIncluidosEncargo,
  precioPdfEncargo,
} from "../../utils/encargoFichaModelos";

const overlay = {
  position: "fixed",
  inset: 0,
  zIndex: 80,
  background: "rgba(15, 23, 42, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
};

const box = {
  background: "#fff",
  borderRadius: 14,
  padding: "18px 20px",
  width: "100%",
  maxWidth: 440,
  boxShadow: "0 18px 50px rgba(0,0,0,0.28)",
};

const chkRow = (on) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  textAlign: "left",
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: on ? "2px solid #1a3a5c" : "1px solid #e2e8f0",
  background: on ? "#e8eef5" : "#f8fafc",
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
});

/** YYYY-MM-DD desde ISO, Date string o DD/MM/YYYY. */
function isoFechaUi(raw) {
  if (raw == null || raw === "") return "";
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, "0");
    const d = String(raw.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(raw).trim();
  const iso = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
  }
  return "";
}

function round2Ui(n) {
  return (Math.round(Number(n) * 100) / 100).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function ModalGenerarEncargoFicha({
  abierto,
  onCerrar,
  onConfirmar,
  propiedadId,
  defaultsExclusiva,
}) {
  const [paso, setPaso] = useState("motivos");
  const [motivos, setMotivos] = useState({ precio: false, modalidad: false, sin_cambios: false });
  const [tipo, setTipo] = useState("");
  const [precioVentaTxt, setPrecioVentaTxt] = useState("");
  const [precioPropTxt, setPrecioPropTxt] = useState("");
  const [duracion, setDuracion] = useState("6 meses");
  const [desde, setDesde] = useState(exclusivaHoyIso());
  const [hasta, setHasta] = useState("");
  const [ajustando, setAjustando] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  /** Si el paso precio vino tras exclusiva (para Atrás). */
  const [precioTrasExclusiva, setPrecioTrasExclusiva] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    setPaso("motivos");
    setMotivos({ precio: false, modalidad: false, sin_cambios: false });
    setTipo("");
    setPrecioTrasExclusiva(false);
    setError("");
    const d0 = EXCL_DURACIONES_PREDEF.includes(defaultsExclusiva?.duracion)
      ? defaultsExclusiva.duracion
      : "6 meses";
    setDuracion(d0);
    const dDesde = isoFechaUi(defaultsExclusiva?.desde) || exclusivaHoyIso();
    setDesde(dDesde);
    setHasta(
      isoFechaUi(defaultsExclusiva?.hasta)
      || exclusivaFechaMasMeses(dDesde, exclusivaMesesDeDuracion(d0)),
    );
    if (!propiedadId) return;
    setCargando(true);
    api.get(`/viafirma/datos-encargo-propiedad/${propiedadId}`)
      .then((r) => {
        const d = r.data || {};
        setCtx(d);
        if (d.precio_venta) setPrecioVentaTxt(formatearPrecioUi(d.precio_venta));
        if (d.precio_propietario) setPrecioPropTxt(formatearPrecioUi(d.precio_propietario));
        else if (d.precio_venta) setPrecioPropTxt(formatearPrecioUi(d.precio_venta));
        if (d.tipo_encargo) setTipo(d.tipo_encargo);
      })
      .catch(() => setCtx(null))
      .finally(() => setCargando(false));
  }, [abierto, propiedadId]);

  const resumenManual = useMemo(() => {
    if (!desde || !hasta) return "";
    const lab = exclusivaLabelMeses(exclusivaMesesEntreFechas(desde, hasta));
    if (!lab || EXCL_DURACIONES_PREDEF.includes(lab)) return "";
    return `Según fechas: ${lab}`;
  }, [desde, hasta]);

  const tipoActual = String(ctx?.tipo_encargo || "").trim();
  const precioVentaActual = ctx?.precio_venta;
  const precioPropActual = ctx?.precio_propietario;
  const ivaPct = Number(ctx?.iva_porcentaje) > 0 ? Number(ctx.iva_porcentaje) : 21;
  const tipoParaPrecio = tipo || tipoActual;
  const incluido = esEncargoHonorariosIncluidos(tipoParaPrecio);

  const honorariosPreview = useMemo(() => {
    const pv = parsePrecioUi(precioVentaTxt);
    if (!pv) return null;
    if (incluido) {
      const pct = pctHonorariosIncluidosEncargo(tipoParaPrecio);
      return pct != null ? calcularHonorariosDesdeVentaYPct(pv, pct, ivaPct) : null;
    }
    const pp = parsePrecioUi(precioPropTxt);
    if (!pp) return null;
    return calcularHonorariosDesdeVentaYProp(pv, pp, ivaPct);
  }, [precioVentaTxt, precioPropTxt, incluido, tipoParaPrecio, ivaPct]);

  if (!abierto) return null;

  const fechasExclusivaActual = () => ({
    desde: isoFechaUi(ctx?.exclusiva_desde) || isoFechaUi(defaultsExclusiva?.desde) || "",
    hasta: isoFechaUi(ctx?.exclusiva_hasta) || isoFechaUi(defaultsExclusiva?.hasta) || "",
  });

  /**
   * Mismo tipo (precio o sin cambios): exclusiva conserva la fecha fin del encargo inicial
   * y no rellena duración en meses.
   */
  const emitirMismoTipo = (tipoEncargo) => {
    const t = tipoEncargo || tipoActual || "Encargo de Venta";
    if (!esTipoEncargoExclusiva(t)) {
      emitir({ tipoEncargo: t });
      return;
    }
    const f = fechasExclusivaActual();
    if (!f.hasta) {
      setError("Esta exclusiva no tiene fecha de fin en la ficha. Indícala en Cabecera o cambia de modalidad.");
      return;
    }
    emitir({
      tipoEncargo: t,
      soloFechaFin: true,
      desde: f.desde || undefined,
      hasta: f.hasta,
    });
  };

  const toggleMotivo = (key) => {
    setMotivos((prev) => {
      if (key === "sin_cambios") {
        return { precio: false, modalidad: false, sin_cambios: !prev.sin_cambios };
      }
      return { ...prev, [key]: !prev[key], sin_cambios: false };
    });
  };

  const emitir = (extra = {}) => {
    const tipoFinal = extra.tipoEncargo || tipo || tipoActual || "Encargo de Venta";
    const lista = [];
    if (motivos.precio) lista.push("precio");
    if (motivos.modalidad) lista.push("modalidad");
    if (motivos.sin_cambios) lista.push("sin_cambios");

    let precioVenta;
    let precioPropietario;
    let hon = null;
    if (motivos.precio) {
      precioVenta = parsePrecioUi(precioVentaTxt);
      if (esEncargoHonorariosIncluidos(tipoFinal)) {
        // Incluidos 4%/5%: el agente solo indica Pventa; honorarios = % + IVA sobre venta;
        // precio propietario = venta − total honorarios (nunca igualar a venta).
        const pct = pctHonorariosIncluidosEncargo(tipoFinal);
        hon = pct != null ? calcularHonorariosDesdeVentaYPct(precioVenta, pct, ivaPct) : null;
        precioPropietario = hon?.precio_propietario != null ? hon.precio_propietario : null;
      } else {
        precioPropietario = parsePrecioUi(precioPropTxt);
        hon = calcularHonorariosDesdeVentaYProp(precioVenta, precioPropietario, ivaPct);
      }
    }

    const precioPdf = motivos.precio
      ? precioPdfEncargo({
        tipoEncargo: tipoFinal,
        precioVenta,
        precioPropietario,
      })
      : undefined;

    onConfirmar({
      tipoEncargo: tipoFinal,
      exclusiva: esTipoEncargoExclusiva(tipoFinal),
      duracion: extra.soloFechaFin ? undefined : extra.duracion,
      desde: extra.desde,
      hasta: extra.hasta,
      soloFechaFin: !!extra.soloFechaFin,
      precio: precioPdf,
      precio_venta: precioVenta,
      precio_propietario: precioPropietario,
      honorarios_euros: hon?.honorarios_euros,
      iva_euros: hon?.iva_euros,
      honorarios_porcentaje: hon?.honorarios_porcentaje,
      total_honorarios: hon?.total_honorarios,
      iva_porcentaje: hon?.iva_porcentaje ?? (motivos.precio ? ivaPct : undefined),
      motivos: lista,
    });
  };

  const seguirDesdeMotivos = () => {
    setError("");
    if (!motivos.precio && !motivos.modalidad && !motivos.sin_cambios) {
      setError("Elige al menos un motivo.");
      return;
    }
    if (motivos.sin_cambios) {
      if (tipoActual) {
        emitirMismoTipo(tipoActual);
        return;
      }
      setPaso("modelos");
      return;
    }
    // Tipo de encargo primero; precio después.
    setPaso("modelos");
  };

  const elegirModelo = (val) => {
    setTipo(val);
    setError("");
    const misma = String(val) === String(tipoActual);
    const necesitaFechasExcl = esTipoEncargoExclusiva(val)
      && (motivos.modalidad || !misma || !fechasExclusivaActual().hasta);
    if (necesitaFechasExcl) {
      setPaso("exclusiva");
      return;
    }
    if (motivos.precio) {
      setPrecioTrasExclusiva(false);
      setPaso("precio");
      return;
    }
    if (esTipoEncargoExclusiva(val) && misma && !motivos.modalidad) {
      emitirMismoTipo(val);
      return;
    }
    emitir({ tipoEncargo: val });
  };

  const aplicarDuracion = (lab) => {
    setDuracion(lab);
    const meses = exclusivaMesesDeDuracion(lab);
    const d = desde || exclusivaHoyIso();
    if (!desde) setDesde(d);
    setAjustando(true);
    setHasta(exclusivaFechaMasMeses(d, meses));
    setAjustando(false);
  };

  const onChangeDesde = (v) => {
    setDesde(v);
    if (ajustando) return;
    const meses = exclusivaMesesDeDuracion(duracion);
    if (meses && EXCL_DURACIONES_PREDEF.includes(duracion) && v) {
      setHasta(exclusivaFechaMasMeses(v, meses));
    }
  };

  const onChangeHasta = (v) => {
    setHasta(v);
    if (ajustando || !desde || !v) return;
    const lab = exclusivaLabelMeses(exclusivaMesesEntreFechas(desde, v));
    if (EXCL_DURACIONES_PREDEF.includes(lab)) setDuracion(lab);
    else setDuracion(lab || duracion);
  };

  const payloadExclusiva = () => {
    const lab = EXCL_DURACIONES_PREDEF.includes(duracion)
      ? duracion
      : exclusivaLabelMeses(exclusivaMesesEntreFechas(desde, hasta)) || duracion;
    return { tipoEncargo: tipo, duracion: lab, desde, hasta };
  };

  const aceptarExclusiva = () => {
    if (!desde || !hasta) return;
    if (motivos.precio) {
      setPrecioTrasExclusiva(true);
      setPaso("precio");
      return;
    }
    emitir(payloadExclusiva());
  };

  const seguirDesdePrecio = () => {
    const pv = parsePrecioUi(precioVentaTxt);
    if (!pv) {
      setError("Indica el precio de venta.");
      return;
    }
    const tipoFinal = tipo || tipoActual || "Encargo de Venta";
    if (!esEncargoHonorariosIncluidos(tipoFinal)) {
      const pp = parsePrecioUi(precioPropTxt);
      if (!pp) {
        setError("Indica el precio propietario.");
        return;
      }
      if (pv <= pp) {
        setError("El precio de venta debe ser mayor que el del propietario.");
        return;
      }
    }
    setError("");
    const misma = String(tipoFinal) === String(tipoActual);
    if (precioTrasExclusiva || (motivos.modalidad && esTipoEncargoExclusiva(tipoFinal) && desde && hasta)) {
      emitir(payloadExclusiva());
      return;
    }
    if (!motivos.modalidad && misma) {
      emitirMismoTipo(tipoFinal);
      return;
    }
    if (esTipoEncargoExclusiva(tipoFinal) && desde && hasta) {
      emitir(payloadExclusiva());
      return;
    }
    emitir({ tipoEncargo: tipoFinal });
  };

  return (
    <div style={overlay} role="presentation">
      <div style={box} role="dialog" aria-modal="true">
        {paso === "motivos" && (
          <>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>Generar nuevo encargo</div>
            <p style={{ margin: "0 0 12px", fontSize: 13, color: "#64748b" }}>
              ¿Por qué lo generas? Puedes marcar una o dos opciones. La ficha no cambia hasta que el documento vuelva firmado por ViaFirma.
            </p>
            {cargando ? <p style={{ fontSize: 13, color: "#64748b" }}>Cargando datos de la ficha…</p> : null}
            {tipoActual ? (
              <p style={{ margin: "0 0 10px", fontSize: 13 }}>
                Encargo actual: <b>{tipoActual}</b>
                {precioVentaActual ? <> · Venta: <b>{formatearPrecioUi(precioVentaActual)} €</b></> : null}
                {precioPropActual && Number(precioPropActual) !== Number(precioVentaActual)
                  ? <> · Prop.: <b>{formatearPrecioUi(precioPropActual)} €</b></>
                  : null}
              </p>
            ) : (
              <p style={{ margin: "0 0 10px", fontSize: 13, color: "#64748b" }}>Aún no hay un tipo de encargo en la ficha.</p>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              <button type="button" style={chkRow(motivos.modalidad)} onClick={() => toggleMotivo("modalidad")}>
                <span>{motivos.modalidad ? "☑" : "☐"}</span>
                <span>1. Cambio de modalidad (tipo de encargo)</span>
              </button>
              <button type="button" style={chkRow(motivos.precio)} onClick={() => toggleMotivo("precio")}>
                <span>{motivos.precio ? "☑" : "☐"}</span>
                <span>2. Nuevo precio</span>
              </button>
              <button type="button" style={chkRow(motivos.sin_cambios)} onClick={() => toggleMotivo("sin_cambios")}>
                <span>{motivos.sin_cambios ? "☑" : "☐"}</span>
                <span>3. Generar sin cambios (el mismo encargo, o uno nuevo si no hay)</span>
              </button>
            </div>
            {error ? <p style={{ color: "#b91c1c", fontSize: 13, margin: "10px 0 0" }}>{error}</p> : null}
            <button type="button" onClick={seguirDesdeMotivos} style={{ marginTop: 14, width: "100%", padding: 10, borderRadius: 8, border: 0, background: "#1a3a5c", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
              Continuar
            </button>
            <button type="button" onClick={onCerrar} style={{ marginTop: 8, width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer" }}>
              Cancelar
            </button>
          </>
        )}

        {paso === "modelos" && (
          <>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>Tipo de encargo</div>
            <p style={{ margin: "0 0 12px", fontSize: 13, color: "#64748b" }}>
              {tipoActual ? <>Ahora tenéis: <b>{tipoActual}</b>. Elige el que quieres ahora.</> : "Elige el modelo."}
              {motivos.precio ? " Después indicarás el precio." : null}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {MODELOS_ENCARGO_APP.map((m) => (
                <button
                  key={m.val}
                  type="button"
                  onClick={() => elegirModelo(m.val)}
                  style={{
                    ...chkRow(m.val === tipoActual),
                    fontWeight: m.val === tipoActual ? 800 : 600,
                  }}
                >
                  {m.val}{m.val === tipoActual ? " (actual)" : ""}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => setPaso("motivos")} style={{ marginTop: 14, width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer" }}>
              Atrás
            </button>
          </>
        )}

        {paso === "exclusiva" && (
          <>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>Exclusiva</div>
            <p style={{ margin: "0 0 12px", fontSize: 13, color: "#64748b", lineHeight: 1.35 }}>
              Botones = atajo (recalculan Hasta). Si cambias el calendario, la duración sigue a las fechas.
            </p>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Duración</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
              {EXCL_DURACIONES_PREDEF.map((lab) => (
                <button
                  key={lab}
                  type="button"
                  onClick={() => aplicarDuracion(lab)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: duracion === lab ? "2px solid #1a3a5c" : "1px solid #e2e8f0",
                    background: duracion === lab ? "#e8eef5" : "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {lab}
                </button>
              ))}
            </div>
            {resumenManual ? (
              <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 600, color: "#1a3a5c" }}>{resumenManual}</p>
            ) : null}
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Desde</label>
            <input type="date" value={desde} onChange={(e) => onChangeDesde(e.target.value)} style={{ width: "100%", marginBottom: 10, padding: 8, borderRadius: 8, border: "1px solid #cbd5e1" }} />
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Hasta</label>
            <input type="date" value={hasta} onChange={(e) => onChangeHasta(e.target.value)} style={{ width: "100%", marginBottom: 16, padding: 8, borderRadius: 8, border: "1px solid #cbd5e1" }} />
            <button type="button" onClick={aceptarExclusiva} style={{ width: "100%", padding: 10, borderRadius: 8, border: 0, background: "#1a3a5c", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
              {motivos.precio ? "Continuar" : "Aceptar"}
            </button>
            <button type="button" onClick={() => setPaso("modelos")} style={{ marginTop: 8, width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer" }}>
              Atrás
            </button>
          </>
        )}

        {paso === "precio" && (
          <>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>Nuevo precio</div>
            <p style={{ margin: "0 0 10px", fontSize: 13, color: "#64748b" }}>
              Tipo: <b>{tipoParaPrecio || "—"}</b>.
              {incluido
                ? " El PDF y la ficha usarán el precio de venta (honorarios incluidos)."
                : " El encargo PDF llevará el precio propietario; Ana avisará por WhatsApp el precio de venta."}
            </p>
            {precioVentaActual ? (
              <p style={{ fontSize: 13, margin: "0 0 8px" }}>
                Actual — venta: <b>{formatearPrecioUi(precioVentaActual)} €</b>
                {precioPropActual ? <> · prop.: <b>{formatearPrecioUi(precioPropActual)} €</b></> : null}
              </p>
            ) : null}
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Precio de venta (€)</label>
            <input
              type="text"
              inputMode="numeric"
              value={precioVentaTxt}
              onChange={(e) => setPrecioVentaTxt(e.target.value)}
              placeholder="Ej. 210000"
              style={{ width: "100%", marginBottom: 12, padding: 8, borderRadius: 8, border: "1px solid #cbd5e1" }}
            />
            {!incluido ? (
              <>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Precio propietario (€)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={precioPropTxt}
                  onChange={(e) => setPrecioPropTxt(e.target.value)}
                  placeholder="Ej. 200000"
                  style={{ width: "100%", marginBottom: 12, padding: 8, borderRadius: 8, border: "1px solid #cbd5e1" }}
                />
              </>
            ) : null}
            {honorariosPreview ? (
              <p style={{ fontSize: 12, color: "#475569", margin: "0 0 10px", lineHeight: 1.4 }}>
                Honorarios (calc.): <b>{round2Ui(honorariosPreview.honorarios_euros)} €</b>
                {" + IVA "}{honorariosPreview.iva_porcentaje}%: <b>{round2Ui(honorariosPreview.iva_euros)} €</b>
                {" · Total: "}<b>{round2Ui(honorariosPreview.total_honorarios)} €</b>
                {" ("}{round2Ui(honorariosPreview.honorarios_porcentaje)}%{")"}
                {incluido && honorariosPreview.precio_propietario != null ? (
                  <>
                    <br />
                    Precio propietario: <b>{formatearPrecioUi(honorariosPreview.precio_propietario)} €</b>
                    {" (venta − total honorarios)"}
                  </>
                ) : null}
              </p>
            ) : null}
            {error ? <p style={{ color: "#b91c1c", fontSize: 13, margin: "0 0 8px" }}>{error}</p> : null}
            <button type="button" onClick={seguirDesdePrecio} style={{ width: "100%", padding: 10, borderRadius: 8, border: 0, background: "#1a3a5c", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
              Continuar
            </button>
            <button
              type="button"
              onClick={() => {
                setError("");
                setPaso(precioTrasExclusiva ? "exclusiva" : "modelos");
              }}
              style={{ marginTop: 8, width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer" }}
            >
              Atrás
            </button>
          </>
        )}
      </div>
    </div>
  );
}
